<script setup lang="ts">
import { useDraggable, useScroll } from '@vueuse/core'

const { data: categories } = await useAsyncData(`blog-categories`, () =>
  $fetch(`/intl-api/blogs/categories`)
)
const route = useRoute()
const router = useRouter()

const keyword = ref(route.query.search?.toString() || '')

const activeCategory = computed(() => route.params.entry?.toString())

const handleSubmit = () => {
  if (keyword.value.trim() === '') {
    return
  }
  router.push(`/blog?search=${encodeURIComponent(keyword.value)}`)
}

const { isMobile } = useBreakpoints()
const scrollContainer = useTemplateRef('scrollContainer')
const { x, arrivedState } = useScroll(scrollContainer, {
  behavior: 'smooth'
})

useDraggable(scrollContainer, {
  preventDefault: true,
  onMove: (_position, event) => {
    if (!scrollContainer.value) return
    scrollContainer.value.scrollLeft -= event.movementX
  }
})

const scroll = (direction: number) => {
  const container = scrollContainer.value
  if (!container) return
  const scrollWidth = container.scrollWidth
  const itemWidth = scrollWidth / container.children.length
  x.value += Math.ceil(direction * itemWidth)
}

watch(keyword, (newKeyword, oldKeyword) => {
  if (newKeyword.trim() === '' && oldKeyword.trim() !== '') {
    router.push('/blog')
  }
})

const { isBelow } = useBelowElement()
</script>

<template>
  <div
    class="bg-background dark:bg-surface-container border-b border-outline-variant sticky top-navbar-m z-30 lg:z-120 lg:top-0 lg:h-navbar"
  >
    <UiContainer class="lg:h-full">
      <div
        class="flex lg:items-center transition-all relative py-2.5 lg:h-full gap-1 lg:gap-4 lg:flex-row flex-col"
        :class="{
          'pl-12': isBelow && !isMobile
        }"
      >
        <Transition name="page" appear>
          <Icon
            v-if="isBelow && !isMobile"
            name="ka-icon:favicon"
            size="32px"
            class="shrink-0 absolute top-1/2 left-0 -translate-y-1/2 border border-outline-variant rounded-md"
          />
        </Transition>
        <div class="lg:grow relative">
          <div
            v-if="!arrivedState.left"
            class="absolute z-1 inset-y-0 -left-1 pr-2 bg-background dark:bg-surface-container flex items-center"
          >
            <UiButton
              class="p-0"
              icon="material-symbols:chevron-left"
              icon-size="sm"
              variant="outline"
              :disabled="arrivedState.left"
              @click="scroll(-1)"
            />
          </div>
          <div
            v-if="!arrivedState.right"
            class="absolute z-1 inset-y-0 -right-1 pl-2 bg-background dark:bg-surface-container flex items-center"
          >
            <UiButton
              class="p-0"
              icon="material-symbols:chevron-right"
              icon-size="sm"
              variant="outline"
              :disabled="arrivedState.right"
              @click="scroll(1)"
            />
          </div>
          <div
            ref="scrollContainer"
            class="flex items-center gap-5 overflow-auto scrollbar-hide"
          >
            <NuxtLink
              to="/blog"
              class="text-nowrap block hover:text-primary cursor-pointer text-sm font-medium text-onBackground py-2 rounded-full"
              :class="{
                'text-primary font-semibold': !activeCategory
              }"
            >
              Semua Kategori
            </NuxtLink>
            <NuxtLink
              v-for="category in categories?.items"
              :key="category.slug"
              :to="`/blog/c/${category.slug}`"
              class="text-nowrap block hover:text-primary cursor-pointer text-sm font-medium text-onBackground py-2 rounded-full"
              :class="{
                'text-primary font-semibold':
                  activeCategory == category.slug?.toString()
              }"
            >
              {{ category.name }}
            </NuxtLink>
          </div>
        </div>
        <form
          class="lg:w-1/5 relative"
          role="search"
          @submit.prevent="handleSubmit"
        >
          <Icon
            name="material-symbols:search"
            class="absolute top-1/2 -translate-y-1/2 left-2.5 text-outline pointer-events-none"
            size="24px"
          />
          <UiInputField
            id="search"
            v-model="keyword"
            type="search"
            name="search"
            class="pl-10 py-2 h-auto placeholder-outline"
            placeholder="Cari Artikel"
            required
          />
        </form>
        <NavbarCta
          v-if="isBelow"
          colorful
          :promo="route.path.startsWith('/promotion')"
          root
        />
      </div>
    </UiContainer>
  </div>
</template>
